import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { timeZoneNames } from 'app/helpers/timezone-options';
import { getDefaultTimezone, loopOnComponents } from 'app/helpers/utils';
import { ValidationHandler } from 'app/helpers/validation.handler';
import { AbstractPickedFile, CloudPickedFile } from 'app/models/picked-file';
import { SheetInfo, SpreadsheetInfo } from 'app/models/spreadsheet-conversion';
import { FormTemplate } from 'app/models/template';
import { FormTemplateService } from 'app/services/form-template.service';
import { SpreadsheetConversionService } from 'app/services/spreadsheet-conversion.service';
import { ModalDirective, ToastService } from 'ng-uikit-pro-standard';
import { Subject } from 'rxjs';
import { filter, finalize, startWith, switchMap, take, tap } from 'rxjs/operators';
import { FieldsPickerComponent } from '../fields-picker/fields-picker.component';

@Component({
  selector: 'app-spreadsheet-to-form',
  templateUrl: './spreadsheet-to-form.component.html',
  styleUrls: ['./spreadsheet-to-form.component.scss']
})
export class SpreadsheetToFormComponent implements OnInit {

  @Input() destPath: string = '';

  formTemplate: FormTemplate = null;

  isLoading: boolean = false;
  isCreatingForm: boolean = false;

  createFormSubmitSubject: Subject<string> = new Subject();

  spreadsheetConversionFormGroup: FormGroup;
  spreadsheetInfo: SpreadsheetInfo;

  assignedDateRadioModel: string = 'now';

  currentColumndIndex = -1;

  @ViewChild('conversionModal', { static: true }) conversionModal: ModalDirective;
  @ViewChild('infoModal', { static: true }) infoModal: ModalDirective;
  @ViewChild('fieldsPicker') fieldsPicker: FieldsPickerComponent;

  // For form creation, data import has a different filter in the HTML pick filter
  supportedFieldTypes = [
    {
      'label': 'Address',
      'type': 'location'
    },
    {
      'label': 'Checkbox',
      'type': 'checkbox'
    },
    {
      'label': 'Currency',
      'type': 'currency'
    },
    {
      'label': 'Text Field',
      'type': 'textfield'
    },
    {
      'label': 'Text Area',
      'type': 'textarea'
    },
    {
      'label': 'Number',
      'type': 'number'
    },
    {
      'label': 'Dropdown',
      'type': 'select'
    },
    {
      'label': 'Radio List',
      'type': 'radio'
    },
    {
      'label': 'Email',
      'type': 'email'
    },
    {
      'label': 'Url',
      'type': 'url'
    },
    {
      'label': 'Phone Number',
      'type': 'phoneNumber'
    },
    {
      'label': 'Date / Time',
      'type': 'datetime'
    },
    {
      'label': 'GeoLocation',
      'type': 'geolocation'
    }
  ];

  constructor(public spreadSheetConversionService: SpreadsheetConversionService, private toastService: ToastService,
              private formBuilder: FormBuilder, private formTemplateService: FormTemplateService,
              private validationHandler: ValidationHandler, private router: Router) {

    this.createFormSubmitSubject.pipe(
      tap(()=> { 
          this.validationHandler.onSubmitCheck(this.spreadsheetConversionFormGroup);
        }),
      switchMap(() =>
        this.spreadsheetConversionFormGroup.statusChanges.pipe(
          startWith(this.spreadsheetConversionFormGroup.status),
          filter(status => status !== 'PENDING'),
          take(1)
        )
      ),
    ).subscribe((status) => {
      if (status == 'VALID') {
        this.startFormCreation();
      } else {
        // invalid, do nothing, errors will be displayed
      }
    });

    this.supportedFieldTypes = this.supportedFieldTypes.sort((a, b) => a.label.localeCompare(b.label));
  }

  ngOnInit(): void {
  }

  startWizard(formTemplate: FormTemplate = null) {
    this.formTemplate = formTemplate;
    this.infoModal.show();
  }

  onPickedFile(e: AbstractPickedFile[]) {
    let storageId: number = (e[0] as CloudPickedFile).storageId;
    let filePath: string = (e[0] as CloudPickedFile).path;

    this.isLoading = true;
    this.spreadSheetConversionService.getSpreadsheetInfo(filePath, storageId).subscribe((info) => {
      this.spreadsheetInfo = info;
      this.initFormGroupFromSpreadsheetInfo(filePath, storageId);
      this.isLoading = false;  
      this.conversionModal.show();
    }, () => {
      this.toastService.error('Error reading file!');
      this.isLoading = false;  
    });
  }

  initFormGroupFromSpreadsheetInfo(filePath: string, storageId: number) {
    this.assignedDateRadioModel = 'now';
    let info: SpreadsheetInfo = this.spreadsheetInfo;

    this.spreadsheetConversionFormGroup = this.formBuilder.group({
      path: [filePath, Validators.required],
      storage_id: [storageId, Validators.required],

      selected_worksheet: [info.sheets[0]? info.sheets[0].name: '', [Validators.required]],

      form_dest_path: [this.destPath? this.destPath: '/', this.formTemplate? null: Validators.required],
      form_name: ['', this.formTemplate? null: [Validators.required, this.formTemplateService.illegalFormCharsValidator]],

      index_basename: ['', this.formTemplate? null: {validators: [Validators.required, Validators.maxLength(160), this.formTemplateService.lowercaseValidator, this.formTemplateService.illegalIndexBasenameStartValidator, this.formTemplateService.illegalIndexBasenameCharsValidator], asyncValidators: this.formTemplateService.dataIndexNameValidator()}],

      submission_name_column_index: [0, Validators.required],

      assigned_datetime: [undefined],
      assigned_datetime_column_index: [undefined],

      timezone: [getDefaultTimezone(), [Validators.required, Validators.maxLength(255)]],
      
      columns: this.getColumnsFormArray(),
      column_headers: [this.getColumnHeaders()]
    });
  }
  getColumnHeaders() {
    let currentSheetInfo: SheetInfo = this.getSelectedSheetInfo();

    return currentSheetInfo.columns.map((v) => v.header);
  }

  getColumnsFormArray() {
    let currentSheetInfo: SheetInfo = this.getSelectedSheetInfo();
    let columnsFormArray: FormArray = this.formBuilder.array([]);

    currentSheetInfo.columns.forEach((c) => {
      columnsFormArray.push(this.formBuilder.group({
        label: [c.header, this.formTemplate? null: this.requiredIfNotSkip],
        form_field_type: ['textfield', this.formTemplate? null: this.requiredIfNotSkip],
        form_field_key: ['', this.formTemplate? this.requiredIfNotSkip: null],

        skip_field_creation: [false],
        is_required: [true],
        skip_data_import: [false],
        add_new_options: [false],

        checkbox_true_value: ['', (c: FormControl) => {
          const form_field_type = c.parent? (c.parent as FormGroup).get('form_field_type'): null;
          if (form_field_type && form_field_type.value && form_field_type.value == 'checkbox' && (!c.value || c.value.length == 0)) {
            return { required: true };
          } else {
            return null;
          }
        }]
      }));
    });
    return columnsFormArray;
  }

  requiredIfNotSkip(c: FormControl) {
    const parent: FormGroup = <FormGroup>c.parent;
    if (!parent)
      return null;
      
    const skipFieldCreation = parent.get('skip_field_creation');
    const skipDataImport = parent.get('skip_data_import');

    if ((skipFieldCreation && skipFieldCreation.value == true) || (skipDataImport && skipDataImport.value == true))
      return null;

    return Validators.required(c);
  }

  getSelectedSheetInfo() {
    if (!this.spreadsheetConversionFormGroup)
      return this.spreadsheetInfo.sheets[0];

    let selectedWorksheet = this.spreadsheetConversionFormGroup.get('selected_worksheet').value;
    return this.spreadsheetInfo.sheets.find((s) => s.name == selectedWorksheet) || this.spreadsheetInfo.sheets[0];
  }

  currentWorksheetChanged() {
    let columnsFormArray = this.getColumnsFormArray();
    this.spreadsheetConversionFormGroup.controls['columns'] = columnsFormArray;
    columnsFormArray.setParent(this.spreadsheetConversionFormGroup);
    this.spreadsheetConversionFormGroup.controls['columns'].updateValueAndValidity();
    this.spreadsheetConversionFormGroup.controls['column_headers'].setValue(this.getColumnHeaders());
  }

  onAssignedDateRadioChange(event) {
    if (this.assignedDateRadioModel == 'now') {
      this.spreadsheetConversionFormGroup.get('assigned_datetime').reset();
      this.spreadsheetConversionFormGroup.get('assigned_datetime_column_index').reset();
    } else if (this.assignedDateRadioModel == 'static') {
      this.spreadsheetConversionFormGroup.get('assigned_datetime_column_index').reset();
    } else if (this.assignedDateRadioModel == 'column') {
      this.spreadsheetConversionFormGroup.get('assigned_datetime').reset();
    }
  }

  timeZoneOptions() : string[] {
    return timeZoneNames;
  }

  startFormCreation() {
    this.isCreatingForm = true;
    let observable = (this.formTemplate)? this.spreadSheetConversionService.fillInForm(this.formTemplate.id, this.spreadsheetConversionFormGroup.value) : this.spreadSheetConversionService.convertToForm(this.spreadsheetConversionFormGroup.value);
    observable.pipe(finalize(() => {
      this.isCreatingForm = false;
    })).subscribe(() => {
      this.toastService.success('Import Started!');
      this.conversionModal.hide();
      this.router.navigate(['/data-imports']);
    }, () => {
      this.toastService.error('Error starting import!');
    });
  }

  onPickField(columnIndex: number) {
    this.currentColumndIndex = columnIndex;
    if (!this.formTemplate.schema) {
      this.isLoading = true;
      this.formTemplateService.getById(this.formTemplate.id).pipe(finalize(() => {
        this.isLoading = false;
      })).subscribe((f) => {
        this.conversionModal.hide();
        this.formTemplate.schema = f.schema;
        this.fieldsPicker.showPicker(this.formTemplate.schema, this.spreadsheetConversionFormGroup.get(`columns.${this.currentColumndIndex}.form_field_key`).value? [this.spreadsheetConversionFormGroup.get(`columns.${this.currentColumndIndex}.form_field_key`).value]: null);
      });
    } else {
      this.conversionModal.hide();
      this.fieldsPicker.showPicker(this.formTemplate.schema, this.spreadsheetConversionFormGroup.get(`columns.${this.currentColumndIndex}.form_field_key`).value? [this.spreadsheetConversionFormGroup.get(`columns.${this.currentColumndIndex}.form_field_key`).value]: null);
    }
  }

  onPickerClosed() {
    this.conversionModal.show();
  }

  onPickedFields(fields: string[]) {
    this.spreadsheetConversionFormGroup.get(`columns.${this.currentColumndIndex}.form_field_key`).setValue(fields[0]);

    // get that field's type to ask for additonal options specific to that type
    loopOnComponents(this.formTemplate.schema, (cmp) => {
      if (cmp.key == fields[0]) {
        this.spreadsheetConversionFormGroup.get(`columns.${this.currentColumndIndex}.form_field_type`).setValue(cmp.type);
      }
    });
  }
}
